import React, { useEffect, useRef, useState } from "react";
import { AppSettingContext } from "../../Application";

import { IRecipientsStepProps } from "@src/models/props/RecipientsStepProps";
import { IRecipientDetails } from "../../../models/RecipientDetails.model";
import { IRecipientsStep, RecipientsStepModel } from "../../../models/RecipientsStep.model";
import { ISenderDetails } from "../../../models/SenderDetails.model";
import LoadingComponent from "../../shared/LoadingComponent";
import RecipientsStepHowToSend from "./RecipientsStepHowToSend";
import RecipientsStepRecipientsDetails from "./RecipientsStepRecipientsDetails";
import RecipientsStepSenderDetails from "./RecipientsStepSenderDetails";
import IScheduleSendDetails from "../../../models/interfaces/ScheduleSendDetails.interface";
import { WorkflowTypeEnum } from "../../../models/enums/WorkflowTypeEnum";
import ScheduleSendDetailsModel from "../../../models/ScheduleSendDetails.model";
import { GiftCardSendTypeEnum } from "../../../models/enums/GiftCardSendTypeEnum";

const RecipientsStep = (props: IRecipientsStepProps) => {

    //global context
    const { debugMode, labels } = React.useContext(AppSettingContext);

    const [pageModel, setPageModel] = useState<IRecipientsStep>(undefined);
    const [recipients, setRecipients] = useState(Array<IRecipientDetails>);
    const [sender, setSender] = useState<ISenderDetails>({ ...props.model.sender });
    const [isDirty, setIsDirty] = useState(false);

    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    //this should be call always
    const checkboxEditRef = useRef(null);
    useEffect(() => {
        checkboxEditRef.current.checked = props.expanded;
    });

    // Init
    useEffect(() => {
        //console.debug('useEffect - init');
        if (pageModel && pageModel.recipients) {
            //in case the user change Qty on Step 1
            if (props.model.recipients.length != pageModel.recipients.length) {
                propsModelUpdating();
                return;
            }
            //below code is not needeed as the model is fixed by propsModelUpdating
            //if (props.model.recipients.length != recipients.length) {
            //    tryToFixTotalRecipients(props.model.recipients.length);
            //    return; //to skip code below. This method will fire again as tryToFixTotalRecipients update recipients
            //}
        }

    }, []);

    useEffect(() => {
        //console.log('useEffect - props.model');
        propsModelUpdating();
    }, [props.model]);

    useEffect(() => {
        setIsLoading(false);
        const result = checkIfModelIsDirty();
        setIsDirty(result);
        props.handleDirtyState(result);

    }, [pageModel, sender, recipients]);

    useEffect(() => {
        //console.debug('useEffect - pageModel');

        if (pageModel && pageModel.recipients) {
            //in case the user change Qty on Step 1
            if (props.model.recipients.length != pageModel.recipients.length) {
                propsModelUpdating();
            }
            else if (props.model.recipients.length != recipients.length) {
                tryToFixTotalRecipients(props.model.recipients.length);
            }
        }
    }, [pageModel]);

    const isValid = () => {
        //VALIDATE object before SAVE
        let copyPageModel = { ...pageModel };
        copyPageModel.sender = { ...sender };
        copyPageModel.recipients = [...recipients];
        return RecipientsStepModel.validateModel(copyPageModel, props.workflowType);
    };

    const checkIfModelIsDirty = (): boolean => {
        if (!pageModel) return false;
        if (!sender) return false;
        if (!recipients) return false;

        //model input:text fields are same like props input:text
        //model.sender.firstName == props.model.sender.firstName
        //model.recipients[i].firstName == props.model.recipients[i].firstName

        // check
        if (pageModel.sendBy != props.model.sendBy) {
            return true;
        }
        if (pageModel.senderSameAsRecipient != props.model.senderSameAsRecipient) {
            return true;
        }
        if (pageModel.sendIndividually != props.model.sendIndividually) {
            return true;
        }
        if (pageModel.sendItLater != props.model.sendItLater) {
            return true;
        }

        //sender
        //Regift - no sender details 
        if (props.workflowType == WorkflowTypeEnum.BUY_GIFT_CARD) {
            if ((sender.firstName != props.model.sender.firstName) ||
                (sender.lastName != props.model.sender.lastName) ||
                (sender.email != props.model.sender.email) ||
                (sender.phone != props.model.sender.phone) ||
                (sender.company != props.model.sender.company)) {
                return true;
            }
        }

        // scheduled details
        if ((pageModel.scheduleSendDetails.date != props.model.scheduleSendDetails.date) ||
            (pageModel.scheduleSendDetails.hour != props.model.scheduleSendDetails.hour) ||
            (pageModel.scheduleSendDetails.minute != props.model.scheduleSendDetails.minute)) {
            return true;
        }

        //recipients
        if (recipients.length != props.model.recipients.length) {
            //debugger;

            return true;
        } else {
            for (var i = 0; i < recipients.length; i++) {
                if ((recipients[i].firstName != props.model.recipients[i].firstName) ||
                    (recipients[i].lastName != props.model.recipients[i].lastName) ||
                    (recipients[i].email != props.model.recipients[i].email) ||
                    (recipients[i].phone != props.model.recipients[i].phone)) {
                    //debugger;

                    return true;
                }
            }

        }

        return false;
    }

    const propsModelUpdating = () => {
        setIsLoading(true);
        //setRecipients(props.model.recipients);
        setPageModel(props.model);
        setRecipients(cloneRecipients());
    };

    const cloneRecipients = (): IRecipientDetails[] => {
        //TODO: in case of length >0 and send individual:false, we can update all records from first one

        // Recipients clone
        const recipientsClone = [] as Array<IRecipientDetails>;
        if (props.model.recipients) {
            for (let i = 0; i < props.model.recipients.length; i++) {
                const recipient = {
                    firstName: props.model.recipients[i].firstName,
                    firstNameIsValid: props.model.recipients[i].firstNameIsValid,
                    lastName: props.model.recipients[i].lastName,
                    lastNameIsValid: props.model.recipients[i].lastNameIsValid,
                    email: props.model.recipients[i].email,
                    emailIsValid: props.model.recipients[i].emailIsValid,
                    phone: props.model.recipients[i].phone,
                };

                recipientsClone.push(recipient);
            }
        }

        return recipientsClone;
    }

    const tryToFixTotalRecipients = (sourceLength: number) => {
        let copyRecipients = Object.assign([], recipients);
        let difference = sourceLength - recipients.length;
        if (difference > 0) {
            //Add items
            for (var i = 0; i < difference; i++) {
                copyRecipients.push({
                    firstName: '', lastName: '', email: '', phone: ''
                });
            }
        } else if (difference < 0) {
            //remove items
            difference *= -1;
            for (var i = 0; i < difference; i++) {
                copyRecipients.pop();
            }
        }

        setRecipients(copyRecipients);
    }

    //Edit/Close
    const handleExpandCheck = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (checkboxEditRef.current.checked) {
            props.handleBeginEdit();
        }
    }

    // RECIPIENTS
    const handleRecipientUpdate = (recipientModel: IRecipientDetails, index: number): void => {
        //console.debug("handleRecipientUpdate", recipientModel, recipients);

        const copyArr = [...recipients];
        copyArr[index].firstName = recipientModel.firstName;
        copyArr[index].lastName = recipientModel.lastName;
        copyArr[index].email = recipientModel.email;
        copyArr[index].phone = recipientModel.phone;
        setRecipients(copyArr);
    };

    // SEND NOW / LATER
    const handleSendItLaterUpdate = (model: IScheduleSendDetails): void => {
        //console.debug("handleSendItLaterUpdate", model);
        let copyPageModel = { ...pageModel };
        copyPageModel.scheduleSendDetails.date = model.date;
        copyPageModel.scheduleSendDetails.hour = model.hour;
        copyPageModel.scheduleSendDetails.minute = model.minute;

        setPageModel(copyPageModel);
    };

    // SENDER
    const handleSenderModelUpdate = (senderModel: ISenderDetails): void => {
        setSender({
            firstName: senderModel.firstName,
            firstNameIsValid: senderModel.firstNameIsValid,
            lastName: senderModel.lastName,
            lastNameIsValid: senderModel.lastNameIsValid,
            email: senderModel.email,
            emailIsValid: senderModel.emailIsValid,
            phone: senderModel.phone,
            company: senderModel.company,
            fullName: `${senderModel.firstName} ${senderModel.lastName}`
        });
    };

    const handleSenderSameAsRecipientChange = (checked: boolean): void => {
        setIsLoading(true);

        //should exists at least one recipient
        setPageModel({ ...pageModel, senderSameAsRecipient: checked });

        if (checked) {
            handleSenderModelUpdate({
                firstName: recipients[0].firstName,
                firstNameIsValid: recipients[0].firstNameIsValid,
                lastName: recipients[0].lastName,
                lastNameIsValid: recipients[0].lastNameIsValid,
                email: recipients[0].email,
                emailIsValid: recipients[0].emailIsValid,
                phone: recipients[0].phone,
                company: "",
                fullName: `${recipients[0].firstName} ${recipients[0].lastName}`
            });
        } else {
            handleSenderModelUpdate({
                firstName: "",
                firstNameIsValid: pageModel.sender.firstNameIsValid,
                lastName: "",
                lastNameIsValid: pageModel.sender.lastNameIsValid,
                email: "",
                emailIsValid: pageModel.sender.emailIsValid,
                phone: "",
                company: "",
                fullName: ""
            });
        }
    }

    const handleSaveAndContinue = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();

        let copyPageModel = { ...pageModel };
        copyPageModel.sender = { ...sender };
        copyPageModel.recipients = [...recipients];

        props.handleSubmit(copyPageModel);
        setIsDirty(false);
    };

    if (error) throw error;
    return (
        <div className="recipients box">
            {/* By default the input is set to checked=false so the show div is hidden */}
            <input
                ref={checkboxEditRef}
                defaultChecked={false}
                onChange={handleExpandCheck}
                type="checkbox" name="recipients-hide" id="recipients-hide" data-recipients-box-hide />

            <div className={`action-card ${props.isActive ? "" : "inactive"}`} style={{ display: 'grid' }}>
                <h3>{labels.get("GiftCard.App.Steps.Recepients")}</h3>

                <div className="edit">
                    {(!props.isSaving && props.isActive) &&
                        <label htmlFor="recipients-hide" className="label-fs" >
                            <i></i>
                        </label>}
                </div>

                <div className="show">
                    {(props.isSaving) && <LoadingComponent />}

                    {(!isLoading && !props.isSaving) &&
                        <div className="slide-content">
                            {debugMode &&
                                <div>
                                    <p>total: {pageModel.recipients.length} : {recipients.length}</p>
                                    <p>sendBy: {pageModel.sendBy}</p>
                                    <p>sendIndividually: {String(pageModel.sendIndividually)}</p>
                                    <p>senderSameAsRecipient: {String(pageModel.senderSameAsRecipient)}</p>
                                    <p>sender: {`${sender.firstName} ${sender.lastName} (${sender.email}, ${sender.company})`}</p>
                                    <p>Send It Later: {String(pageModel.sendItLater)}, date:
                                        {`${pageModel.scheduleSendDetails.date} ${pageModel.scheduleSendDetails.hour}:${pageModel.scheduleSendDetails.minute}`}</p>
                                    <br />
                                    {recipients.length > 0 && <>
                                        <p>RECEPIENT 1:</p>
                                        <p>{`${recipients[0].firstName} ${recipients[0].lastName} (${pageModel.recipients[0].email})`}</p>
                                        <br />
                                    </>}

                                    <p>Is dirty: {String(isDirty)}</p>
                                    <p>Is valid: {String(isValid())}</p>
                                </div>}

                            <RecipientsStepHowToSend
                                sendBy={pageModel.sendBy}//local version
                                handleSendByChange={(sendBy) => {
                                    if (sendBy === GiftCardSendTypeEnum.BY_PDF) {
                                        setPageModel({
                                            ...pageModel,
                                            sendBy: sendBy,
                                            sendItLater: false,
                                            scheduleSendDetails: new ScheduleSendDetailsModel()
                                        });
                                    } else {
                                        setPageModel({
                                            ...pageModel,
                                            sendBy: sendBy,
                                        });
                                    }
                                }}
                            />

                            <hr />
                            <RecipientsStepRecipientsDetails
                                recipients={recipients}
                                sendBy={pageModel.sendBy}
                                sendIndividually={pageModel.sendIndividually}

                                sendItLater={pageModel.sendItLater} //local version
                                scheduleSendDetails={pageModel.scheduleSendDetails} //local version

                                handleSendIndividualyChange={(checked) => setPageModel({ ...pageModel, sendIndividually: checked })}
                                handleChangeSendItLater={(checked) => setPageModel({ ...pageModel, sendItLater: checked })}
                                handleRecipientUpdate={(recipient, index) => handleRecipientUpdate(recipient, index)}

                                handleSendItLaterUpdate={(model) => handleSendItLaterUpdate(model)}
                            />

                            {/* Manage recipients only in Buy gift workflow 
                            {props.workflowType == WorkflowTypeEnum.BUY_GIFT_CARD &&} */}
                            <RecipientsStepSenderDetails
                                sender={sender} //local version
                                hasToShowSameAsRecipientToggle={recipients.length == 1}
                                senderSameAsRecipient={pageModel.senderSameAsRecipient}

                                handleSenderSameAsRecipientChange={(checked) => handleSenderSameAsRecipientChange(checked)}
                                handleModelUpdate={(senderModel) => handleSenderModelUpdate(senderModel)}
                            />
                            <hr />

                            <button className="btn btn__primary btn__full_width btn__large large-fs"
                                disabled={!isValid() || !isDirty}
                                onClick={handleSaveAndContinue}>{labels.get("GiftCard.App.Steps.Recepients.Btn.Submit")}</button>

                        </div>}
                </div>
            </div>
        </div>
    )
}

export default RecipientsStep;